// WHO DRAWS THE ENVIRONMENT — decided once, before it loads.
//
// THE PROBLEM THIS SOLVES. `Scene.jsx` mounts the environment on its URL alone,
// and whether three's overlay will draw it is settled much later, inside
// `OGSModel`, after the ORT device exists and the branches have lift buffers. So
// the environment used to commit to Spark's decoder before anyone knew three
// would be drawing it — and the bridge then ADOPTED whatever Spark produced.
// That inversion is what kept Spark on the critical path: the two renderers want
// different in-memory representations (Spark's `PackedSplats` against three's
// `BufferGeometry`), so deciding late means either writing a converter between
// them or paying a second full parse (1.8 s and tens of MB on Startup_Level).
//
// So the decision is hoisted here and the environment is REQUESTED by its owner
// rather than adopted after the fact.
//
// A MODULE SINGLETON, not React state, for the same reason `graphState` and
// `liveBodyRig` are: the readers are a render-loop component and an imperative
// renderer, and a re-render is not how either of them should learn this.
//
// TWO OWNERS, BOTH THREE. Spark is gone, so this no longer picks a RENDERER —
// it picks WHICH MESH the environment's splats live in, and that is still a real
// decision with real consequences:
//
//   'scene'   the app scene draws it (`ThreeEnvironmentSplat`), in its own
//             `GaussianSplat`. Composites with a GLB avatar, the ground and the
//             shadow catcher, because they are all in one scene.
//   'avatar'  the splat avatar's own bridge mesh draws it, so every splat in the
//             frame — face and room — is in ONE mesh and depth-sorts together.
//             A `GaussianSplat` sorts only its own splats, which is why this is
//             not merely an optimisation: two meshes order by `renderOrder` and
//             the room paints over the face.
//
// THE DEFAULT IS 'scene', deliberately. Every path that fails to decide — an
// unrecognised character, a probe that throws, a code path that forgets to call
// `decideOwner` — lands on the environment being DRAWN rather than on one drawn
// by nobody. That failure mode is not hypothetical: a blank canvas renders very
// fast and reads as a working renderer.

/** @typedef {'scene'|'avatar'} Owner */

const state = {
  /** @type {Owner} */
  owner: 'scene',
  /** Why it is what it is — shown in the console, never guessed at. */
  reason: 'not yet decided',
  decided: false,
  /** What to draw: `{ url, version, position, rotation, scale }` or null. */
  env: null,
  /** The floor the environment reported, world-space Y, or null. See
   *  `setOwnedFloorY` — this is the channel the AVATAR-owned path reports on. */
  floorY: null,
  /** Why the environment could not be DECODED, or null. Distinct from ownership:
   *  `handBackToScene` means "this renderer cannot draw it, let the other try",
   *  which is only worth doing when the other one MIGHT. Broken bytes fail
   *  identically wherever they are parsed, so handing those back buys a second
   *  43 MB download and a second identical failure. See `setEnvironmentBroken`. */
  broken: null,
}

const listeners = new Set()
const notify = () => { for (const cb of listeners) { try { cb(read()) } catch { /* a bad listener is not the store's problem */ } } }

export function read() {
  return {
    owner: state.owner, reason: state.reason, decided: state.decided,
    env: state.env, floorY: state.floorY, broken: state.broken,
  }
}

export function subscribe(cb) {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

/**
 * The environment the scene wants drawn, whoever ends up drawing it. Called by
 * `Scene` whenever the URL or its pose changes; null when there is none.
 */
export function setEnvironment(env) {
  const same = JSON.stringify(state.env) === JSON.stringify(env)
  if (same) return
  // A floor belongs to the room that reported it. Keeping it across a change
  // would place the avatar against the PREVIOUS environment until the new one
  // finishes loading, which is a plausible-looking wrong answer rather than a
  // visible gap.
  state.floorY = null
  // A new environment has not failed yet. Keeping the old verdict would hide a
  // good file behind a broken one's error.
  state.broken = null
  state.env = env
  notify()
}

/**
 * Settle the owner BEFORE the environment loads.
 *
 * @param {object} o
 * @param {boolean} o.splatCharacter  the avatar's mesh only owns it when there
 *   IS a splat avatar — one mesh, one sort. A GLB character has no such mesh, so
 *   the environment goes in the scene alongside the avatar, the ground and the
 *   shadow catcher.
 * @param {() => Promise<string|null>} o.deviceRefusal  the hoistable half of
 *   `overlayRefusal` — WebGPU presence and a limits check.
 */
export async function decideOwner({ splatCharacter, deviceRefusal }) {
  if (!splatCharacter) return set('scene', 'a GLB character has no splat mesh for the environment to join')
  let refusal = null
  try {
    refusal = await deviceRefusal()
  } catch (e) {
    return set('scene', `device probe threw: ${e?.message ?? e}`)
  }
  if (refusal) return set('scene', refusal)
  return set('avatar', 'the avatar is splats, so the environment joins its mesh and sorts with it')
}

/**
 * The LATE refusal. The branch half of `overlayRefusal` cannot be hoisted — a
 * lifter's device buffer only exists once the pipeline built one — so the avatar
 * mesh can still lose the environment after this said it would own it. Handing
 * it back is what keeps that from ending in an environment nobody draws.
 */
/** THE FLOOR THE AVATAR-OWNED ENVIRONMENT REPORTED, or null.
 *
 * `Scene.jsx` owns `__ENV_FLOOR_Y__` and gets it from `ThreeEnvironmentSplat`'s
 * `onFloorY` — but that component does not mount when the AVATAR owns the
 * environment, so nothing reported it for a splat character and the avatar, the
 * ground plane and the shadow sun all sat at 0 whatever the room said. The bridge
 * loads that environment, so it publishes here and Scene subscribes: one channel,
 * rather than a second component mounted only to re-parse 43 MB for one number.
 */
export function setOwnedFloorY(y) {

  const next = Number.isFinite(y) ? y : null
  if (next === state.floorY) return
  state.floorY = next
  notify()

}

/**
 * THE ENVIRONMENT'S BYTES ARE BAD — not this renderer's problem, everyone's.
 *
 * Reported instead of `handBackToScene` when the failure is a decode rather than
 * a capability: a truncated or corrupt `.spz` fails the same way in whichever
 * component parses it, so handing it back costs a second full download and a
 * second identical failure, and leaves the scene believing it has an environment
 * that nothing draws. That is what renders as a BLACK viewport — the no-environment
 * fallback (studio stage, grid, backdrop) is gated on there being no environment,
 * and there is one, it just cannot be decoded.
 *
 * @param {string} reason - what the parse said, shown to the user.
 */
export function setEnvironmentBroken(reason) {

  const next = reason ? String(reason) : null
  if (next === state.broken) return read()
  state.broken = next
  if (next) console.error(`[env-owner] the environment could not be decoded: ${next}`)
  notify()
  return read()

}

export function handBackToScene(reason) {
  if (state.owner === 'scene') return read()
  console.warn(`[env-owner] the avatar mesh gave the environment back to the scene: ${reason}`)
  return set('scene', reason)
}

function set(owner, reason) {
  const changed = state.owner !== owner || state.reason !== reason
  state.owner = owner
  state.reason = reason
  state.decided = true
  if (changed) notify()
  return read()
}

/** Test seam + a character switch: forget the decision and the environment. */
export function resetEnvironmentOwner() {
  state.owner = 'scene'
  state.reason = 'not yet decided'
  state.decided = false
  state.env = null
  notify()
}

if (typeof window !== 'undefined') {
  window.__ENV_OWNER__ = { read, subscribe, handBackToScene, reset: resetEnvironmentOwner }
}
